/**
 * Aide CONTEXTUELLE (migration 0300) : les questions de la FAQ rattachées à un
 * endroit de l'app par une clé (`faq_items.context_key`). Une seule requête,
 * gardée en cache : toutes les pastilles « ? » de l'app la partagent.
 *
 * `faq_items` est postérieure aux types générés → client générique.
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface HelpTopic {
  key: string;
  question: string;
  answer_md: string;
}

/** Clés valides (miroir du CHECK SQL `faq_items_context_key_chk`). */
export const HELP_KEY_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;

/** Normalise une saisie admin en clé valide, ou null (champ vide / invalide). */
export function normalizeHelpKey(input: string): string | null {
  const key = input
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return key && HELP_KEY_PATTERN.test(key) ? key : null;
}

/**
 * Où chaque clé est posée dans l'app (aide à l'administration : la page FAQ les
 * propose). Ajouter une pastille = `<HelpTip topic="…" />` + une ligne ici.
 */
export const HELP_TOPIC_PLACES: Readonly<Record<string, string>> = {
  hora: 'SKILLCARD — devant « HORA » (bloc Ma maîtrise) + tuile « HORA » du tableau de bord élève',
  deck: 'SKILLCARD — « Ton deck » (offres d’achat : page d’achat et fin d’exercice)',
  diagnostic:
    'Diagnostic de programme — tableau de bord élève (carte et tuile « Programme ») et écran du diagnostic',
  automatismes: 'Automatismes — carte du tableau de bord élève et page des automatismes',
  xp: 'Tableau de bord élève — tuile « Points XP »',
  reussite: 'Tableau de bord élève — tuile « Réussite »',
  moyenne: 'Tableau de bord élève — tuile « Moyenne »',
  brouillon: 'Feuille de brouillon — carte « Ma feuille de brouillon » d’une question',
  'parent-exercices': 'Accueil parent — carte de l’enfant, chiffre « exercices »',
  'parent-reussite': 'Accueil parent — carte de l’enfant, chiffre « réussite »',
  'parent-hora': 'Accueil parent — carte de l’enfant, chiffre « HORA »',
  'parent-serie': 'Accueil parent — carte de l’enfant, chiffre « série »',
};

const db = supabase as unknown as SupabaseClient;

export function useHelpTopics() {
  return useQuery({
    queryKey: ['help-topics'],
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<Map<string, HelpTopic>> => {
      const { data, error } = await db
        .from('faq_items')
        .select('context_key, question, answer_md')
        .eq('is_published', true)
        .not('context_key', 'is', null);
      if (error) throw new Error(error.message);
      const map = new Map<string, HelpTopic>();
      for (const row of (data ?? []) as {
        context_key: string;
        question: string;
        answer_md: string;
      }[]) {
        map.set(row.context_key, {
          key: row.context_key,
          question: row.question,
          answer_md: row.answer_md,
        });
      }
      return map;
    },
  });
}

/** L'aide d'une clé, ou null (pas encore chargée, absente ou dépubliée). */
export function useHelpTopic(key: string): HelpTopic | null {
  return useHelpTopics().data?.get(key) ?? null;
}
